'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Icon } from '@/components/ui/icon';
import { NutritionBreakdownModal } from './nutrition-breakdown-modal';
import type { IngredientWithDetails } from '@/lib/data/recipes';
import { GDA, gdaPct } from '@/lib/utils/gda';
import { joinAmount } from '@/lib/utils/format-amount';
import styles from './nutrition-panel.module.css';

// Atwater factors (kcal per gram) — split total energy across the macros.
const MACRO_KCAL = { protein: 4, carbs: 4, fat: 9 } as const;
// Unit symbols hoisted so they don't trip i18next/no-literal-string (universal).
const KCAL_UNIT = 'kcal';
const GRAM_UNIT = 'g';
const PERCENT = '%';
// Placeholder shown in the calorie slot when nutrition hasn't been computed yet.
const NA_DASH = '—';

type Tone = 'accent' | 'warn' | 'sage' | 'neutral';

interface NutritionPanelProps {
  /** Null when this variant's nutrition hasn't been computed yet — the panel
   *  then renders its own "not calculated" state instead of the macro rows. */
  calories: number | null;
  proteinG: number;
  carbsG: number;
  fatG: number;
  fiberG: number;
  ingredients?: IngredientWithDetails[];
  servings?: number;
  /** What one serving physically is ("1 scone"). Null/undefined for dishes with
   *  no discrete unit — the eyebrow then reads plain "Per serving" as before.
   *  This is the figure's unit, so it belongs on the number, not in a footnote:
   *  where servings counts PEOPLE but the dish is handed out in pieces, the two
   *  diverge and this is the only thing that says which one the kcal is for. */
  servingUnit?: string | null;
  /**
   * True when the figure is the model's estimate of the whole dish rather than
   * arithmetic over the ingredient list (#867). Renders a short qualifier, so
   * an estimate never reads as a measurement — a guess and a calculation must
   * not be pixel-identical (CLAUDE.md §20).
   */
  isEstimated?: boolean;
}

export function NutritionPanel({
  calories,
  proteinG,
  carbsG,
  fatG,
  fiberG,
  ingredients,
  servings,
  servingUnit,
  isEstimated = false,
}: NutritionPanelProps) {
  const t = useTranslations('recipes.nutritionPanel');
  // Reuses the existing recipes.nutritionNotComputed copy for the empty state.
  const tRecipes = useTranslations('recipes');
  const [showBreakdown, setShowBreakdown] = useState(false);

  const eyebrow = servingUnit ? t('perServingUnit', { unit: servingUnit }) : t('perServing');

  // Nutrition not computed yet: render the panel shell with an explicit note so
  // the section reads as intentional rather than broken.
  if (calories === null) {
    return (
      <div className={styles.panel} role="group" aria-label={t('panelAria')}>
        <div className={styles.head}>
          <div>
            <div className={styles.eyebrow}>{eyebrow}</div>
            <div className={styles.cal}>
              <span className={`${styles.calNum} ${styles.calNa}`}>{NA_DASH}</span>
            </div>
          </div>
        </div>
        <p className={styles.note}>{tRecipes('nutritionNotComputed')}</p>
      </div>
    );
  }

  const canShowBreakdown =
    ingredients != null &&
    servings != null &&
    ingredients.some((i) => i.caloriesPer100g != null);

  const rows: Array<{
    key: 'protein' | 'carbs' | 'fat' | 'fibre';
    label: string;
    grams: number;
    gda: number;
    tone: Tone;
  }> = [
    { key: 'protein', label: t('protein'), grams: proteinG, gda: gdaPct(proteinG, GDA.proteinG), tone: 'accent' },
    { key: 'carbs', label: t('carbs'), grams: carbsG, gda: gdaPct(carbsG, GDA.carbsG), tone: 'warn' },
    { key: 'fat', label: t('fat'), grams: fatG, gda: gdaPct(fatG, GDA.fatG), tone: 'sage' },
    { key: 'fibre', label: t('fibre'), grams: fiberG, gda: gdaPct(fiberG, GDA.fiberG), tone: 'neutral' },
  ];

  // Calorie-source split — only the energy-bearing macros (protein/carbs/fat).
  const energyRows = rows.filter((r): r is typeof r & { key: 'protein' | 'carbs' | 'fat' } => r.key !== 'fibre');
  const totalKcal = energyRows.reduce((sum, r) => sum + r.grams * MACRO_KCAL[r.key], 0);
  const showSplit = totalKcal > 0;
  const segPct = (r: (typeof energyRows)[number]) =>
    Math.round(((r.grams * MACRO_KCAL[r.key]) / totalKcal) * 100);

  return (
    <>
      <div className={styles.panel} role="group" aria-label={t('panelAria')}>
        <div className={styles.head}>
          <div>
            <div className={styles.eyebrow}>{eyebrow}</div>
            <div className={styles.cal}>
              <span className={styles.calNum}>{Math.round(calories)}</span>
              <span className={styles.calUnit}>{KCAL_UNIT}</span>
            </div>
          </div>
        </div>

        {showSplit && (
          <div className={styles.split}>
            <span className={styles.splitLabel}>{t('calorieSources')}</span>
            <div className={styles.splitBar} aria-hidden="true">
              {energyRows.map((r) => (
                <span
                  key={r.key}
                  className={`${styles.seg} ${styles[`seg_${r.tone}`]}`}
                  style={{ width: `${segPct(r)}%` }}
                />
              ))}
            </div>
            <div className={styles.legend}>
              {energyRows.map((r) => (
                <span key={r.key} className={styles.legendItem}>
                  <span className={`${styles.dot} ${styles[`dot_${r.tone}`]}`} aria-hidden="true" />
                  {r.label}
                  <b>{segPct(r)}{PERCENT}</b>
                </span>
              ))}
            </div>
          </div>
        )}

        <dl className={styles.macros}>
          {rows.map((m) => (
            <div key={m.key} className={styles.macro}>
              <dt className={styles.mLabel}>{m.label}</dt>
              <dd className={styles.mVal}>{joinAmount(String(Math.round(m.grams)), GRAM_UNIT)}</dd>
              <span className={styles.gda}>
                <span aria-label={t('gdaAria', { pct: m.gda })}>{m.gda}{PERCENT}</span>{' '}
                <span className={styles.gdaOf}>{t('daily')}</span>
              </span>
              <div className={`${styles.bar} ${styles[`bar_${m.tone}`]}`} aria-hidden="true">
                <span style={{ width: `${Math.max(0, Math.min(100, m.gda))}%` }} />
              </div>
            </div>
          ))}
        </dl>

        {isEstimated && (
          <p className={styles.estimatedNote}>
            <Icon name="info" size={14} strokeWidth={1.75} aria-hidden="true" />
            {t('estimatedNote')}
          </p>
        )}

        <p className={styles.note}>{t('gdaNote')} {t('disclaimer')}</p>

        {canShowBreakdown && (
          <button type="button" className={styles.more} onClick={() => setShowBreakdown(true)}>
            {t('breakdown')}
            <Icon name="chevron-right" size={16} strokeWidth={1.75} />
          </button>
        )}
      </div>

      {showBreakdown && canShowBreakdown && (
        <NutritionBreakdownModal
          ingredients={ingredients!}
          servings={servings!}
          onClose={() => setShowBreakdown(false)}
        />
      )}
    </>
  );
}
